import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme/theme';
import 'bootstrap/dist/css/bootstrap.min.css';

const normalizeSpaFallbackUrl = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const { pathname, search, hash } = window.location;
  const hasMalformedSpaQuery = search === '?/' || search.startsWith('?/') || search.includes('~and~');

  if (!hasMalformedSpaQuery) {
    return;
  }

  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';

  if (normalizedPathname !== '/') {
    window.history.replaceState(null, '', `${normalizedPathname}${hash}`);
    return;
  }

  const queryBody = search.replace(/^\?\//, '');
  const [encodedPath = '', ...encodedQueryParts] = queryBody.split('&');
  const restoredPath = encodedPath
    ? `/${encodedPath.replace(/~and~/g, '&')}`.replace(/\/+/g, '/')
    : '/';
  const restoredQuery = encodedQueryParts.length > 0
    ? `?${encodedQueryParts.join('&').replace(/~and~/g, '&')}`
    : '';

  window.history.replaceState(null, '', `${restoredPath}${restoredQuery}${hash}`);
};

normalizeSpaFallbackUrl();

// ---------------------------------------------------------------------------
// Patch DOM methods to handle Google Translate interference.
//
// Google Translate wraps text nodes inside <font> tags, effectively
// re-parenting them.  When React later tries to insertBefore / removeChild
// using a reference to the original (now re-parented) node, the browser
// throws "NotFoundError: The node before which the new node is to be
// inserted is not a child of this node."
//
// This is the well-known React issue #11538.  The standard fix is to
// make these two DOM methods resilient to the mismatch.
// ---------------------------------------------------------------------------
if (typeof Node !== 'undefined' && Node.prototype) {
  const _originalInsertBefore = Node.prototype.insertBefore;
  // @ts-ignore – intentional low-level patch
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was moved (likely by Google Translate). Fall back to
      // appendChild so React can continue instead of crashing.
      return this.appendChild(newNode) as unknown as T;
    }
    return _originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  const _originalRemoveChild = Node.prototype.removeChild;
  // @ts-ignore – intentional low-level patch
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      // Child was re-parented by Google Translate; nothing to remove.
      return child;
    }
    return _originalRemoveChild.call(this, child) as T;
  };
}
// ---------------------------------------------------------------------------

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <HelmetProvider>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);
